import React, { createContext, useContext, useMemo, useReducer } from "react";

// Data model types
export type ID = string;

export type POStatus = "draft" | "review" | "approved" | "delivered";
export type InvoiceStatus = "draft" | "pending" | "partial" | "paid" | "overdue";

export interface Customer {
  id: ID;
  name: string;
  metrics?: {
    totalPOs?: number;
    outstanding?: number;
  };
}

export interface SupplierAddress {
  id: ID;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  pincode: string;
  gstin: string;
}

export interface SupplierBankDetails {
  account_holder_name?: string;
  bank_name?: string;
  branch_name?: string;
  ifsc_code?: string;
  account_number?: string;
}

export interface Supplier {
  id: ID;
  // Company Information
  company_name: string;
  trade_name?: string;
  
  // Primary Contact
  primary_contact_name: string;
  primary_contact_mobile: string;
  primary_contact_email: string;
  
  // Bank Details (optional at onboarding, mandatory before invoice)
  bank_details?: SupplierBankDetails;
  
  // Addresses (repeatable)
  addresses: SupplierAddress[];
  
  // Status and metadata
  status: "active" | "inactive" | "pending_verification";
  created_at: string;
  updated_at: string;
  
  // Legacy fields for backward compatibility
  name?: string;
  gstin?: string;
  contact?: string;
  mobile?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface IndividualAsset {
  id: ID;
  assetLineId: ID;
  uniqueAssetId: string; // Format: assetLineId-1, assetLineId-2, etc.
  poId: ID;
  invoiceId?: ID; // Only set when included in an invoice
  specification?: string;
  serialNumber?: string;
  assetCategory?: string;
  status?: "pending" | "in_process" | "shipped" | "delivered";
  
  // Inherited from parent AssetLine
  item?: string;
  description?: string;
  unitPrice?: number;
  hsn?: string;
  taxRate?: number;
  cgst?: number;
  sgst?: number;
  igst?: number;
  utgst?: number;
  gstTreatment?: "cgst_sgst" | "igst" | "cgst_utgst";
  
  // Tracking and delivery details
  trackingId?: string;
  shippingCompany?: string;
  shippedFrom?: string;
  deliveryAddress?: string;
  eBayBillUrl?: string;
  loadingReceiptUrl?: string;
  deliveryProofUrl?: string;
  lastUpdated?: string;
  statusHistory?: Array<{
    status: string;
    date: string;
    notes?: string;
    updatedBy?: string;
    trackingId?: string;
    shippingCompany?: string;
    attachments?: string[];
  }>;
  attachments?: string[];
  notes?: string;
  // Insurance details
  insuranceStartDate?: string;
  insuranceEndDate?: string;
  insuranceProvider?: string;
  transitInsuranceUrl?: string;
  assetInsuranceUrl?: string;
  // Warranty details
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  warrantyProvider?: string;
}

export interface AssetLine {
  id: ID;
  item: string;
  description: string;
  hsn?: string;
  unitPrice: number;
  qty: number;
  taxRate?: number;
  assetType?: string;
  cgst?: number;
  sgst?: number;
  igst?: number;
  utgst?: number;
  status?: "pending" | "in_process" | "shipped" | "delivered";
  trackingId?: string;
  shippingCompany?: string;
  shippedFrom?: string;
  deliveryAddress?: string;
  eBayBillUrl?: string;
  loadingReceiptUrl?: string;
  deliveryProofUrl?: string;
  lastUpdated?: string;
  gstTreatment?: "cgst_sgst" | "igst" | "cgst_utgst";
  statusHistory?: Array<{
    status: string;
    date: string;
    notes?: string;
    updatedBy?: string;
    trackingId?: string;
    shippingCompany?: string;
    attachments?: string[];
  }>;
  attachments?: string[];
  notes?: string;
}

export interface PurchaseOrder {
  id: ID;
  customerId: ID;
  supplierId: ID;
  status: POStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deliveryAddress?: string;
  approvals?: {
    underwriter: boolean;
    comments?: string;
  };
  attachments?: string[];
  lines: AssetLine[];
}

export interface Invoice {
  id: ID;
  poId: ID;
  number: string;
  date: string; // ISO
  dueDate?: string; // ISO
  amount: number; // base amount before tax
  tax: number; // absolute tax amount
  assetLineIds?: ID[];
  attachmentUrl?: string;
  taxInvoiceUrl?: string;
  paymentProofUrl?: string;
  status: InvoiceStatus;
    // Section 194Q TDS fields
  previousPurchaseValue?:number;
  tds_amount?: number;
  tds_exempted?: number; // Toggle for special exemption cases
  e_way_status?: string | null;
}

export interface Payment {
  id: ID;
  invoiceId: ID;
  date: string;
  mode: string;
  reference?: string;
  amount: number;
  notes?: string;
  receiptUrl?: string;
}

// Helpers
const now = () => new Date().toISOString();
const uuid = () => Math.random().toString(36).slice(2, 10);

export function poTotals(lines: AssetLine[]) {
  const subTotal = lines?.reduce((s, l) => s + (l.unitPrice) * l.qty, 0);
  const tax = lines?.reduce((s, l) => s + ((l.unitPrice) * l.qty * (l.taxRate || 0)) / 100, 0);
  return { subTotal, tax, total: subTotal + tax };
}

export function invoiceOutstanding(invoice: Invoice, payments: Payment[]) {
  const paid = payments
    .filter((p) => p.invoiceId === invoice.id)
    .reduce((s, p) => s + p.amount, 0);
  const due = invoice.amount + invoice.tax;
  return Math.max(0, +(due - paid).toFixed(2));
}

// State
interface State {
  customers: Customer[];
  suppliers: Supplier[];
  purchaseOrders: PurchaseOrder[];
  invoices: Invoice[];
  payments: Payment[];
  individualAssets: IndividualAsset[];
}

// Initial sample data
// Helper function to generate individual assets from asset lines
const generateIndividualAssets = (poId: ID, assetLine: AssetLine): IndividualAsset[] => {
  const assets: IndividualAsset[] = [];
  for (let i = 1; i <= assetLine.qty; i++) {
    assets.push({
      id: `${assetLine.id}_asset_${i}`,
      assetLineId: assetLine.id,
      uniqueAssetId: `${assetLine.id}-${i}`,
      poId,
      status: "pending",
      // Inherit properties from parent asset line
      item: assetLine.item,
      description: assetLine.description,
      unitPrice: assetLine.unitPrice,
      hsn: assetLine.hsn,
      taxRate: assetLine.taxRate,
      cgst: assetLine.cgst,
      sgst: assetLine.sgst,
      igst: assetLine.igst,
      utgst: assetLine.utgst,
      gstTreatment: assetLine.gstTreatment,
    });
  }
  return assets;
};

const initialState: State = {
  customers: [
    { id: "cust_1", name: "Acme Corp", metrics: { totalPOs: 2, outstanding: 34000 } },
    { id: "cust_2", name: "Globex Ltd" },
  ],
  suppliers: [
    { 
      id: "sup_1", 
      company_name: "Aero Supplies",
      primary_contact_name: "John Smith",
      primary_contact_mobile: "9876543210",
      primary_contact_email: "john@aerosupplies.com",
      addresses: [
        {
          id: "addr_1",
          address_line_1: "123 Industrial Area",
          city: "Mumbai",
          state: "27",
          pincode: "400001",
          gstin: "29ABCDE1234F1Z5"
        }
      ],
      status: "active" as const,
      created_at: now(),
      updated_at: now(),
      // Legacy compatibility
      name: "Aero Supplies", 
      gstin: "29ABCDE1234F1Z5", 
      contact: "John Smith",
      mobile: "9876543210",
      email: "john@aerosupplies.com",
      address: "123 Industrial Area"
    },
    { 
      id: "sup_2", 
      company_name: "Quantum Parts",
      primary_contact_name: "Sarah Johnson", 
      primary_contact_mobile: "9876543211",
      primary_contact_email: "sarah@quantumparts.com",
      addresses: [
        {
          id: "addr_2",
          address_line_1: "456 Tech Park",
          city: "Bangalore",
          state: "29",
          pincode: "560001",
          gstin: "29XYZAB5678C2D3"
        }
      ],
      status: "pending_verification" as const,
      created_at: now(),
      updated_at: now(),
      // Legacy compatibility
      name: "Quantum Parts",
      contact: "Sarah Johnson",
      mobile: "9876543211",
      email: "sarah@quantumparts.com",
      address: "456 Tech Park"
    },
  ],
  purchaseOrders: [
    {
      id: "po_1001",
      customerId: "cust_1",
      supplierId: "sup_1",
      status: "review",
      createdBy: "ops.jane",
      createdAt: now(),
      updatedAt: now(),
      approvals: { underwriter: false },
      lines: [
        { id: "line_1", item: "Industrial Fan", description: "High-efficiency industrial cooling fan", hsn: "8414", unitPrice: 12000, qty: 2, taxRate: 18 },
        { id: "line_2", item: "Motor Bearings", description: "Premium quality motor bearings set", hsn: "8482", unitPrice: 1500, qty: 6, taxRate: 18 },
      ],
    },
  ],
  invoices: [
    { id: "inv_9001", poId: "po_1001", number: "AF-2025-001", date: now(), amount: 12000, tax: 2160, status: "pending" },
  ],
  payments: [
    { id: "pay_7001", invoiceId: "inv_9001", date: now(), mode: "NEFT", reference: "NEFT123", amount: 5000 },
  ],
  individualAssets: [
    // Generate initial individual assets for the sample PO
    ...generateIndividualAssets("po_1001", { id: "line_1", item: "Industrial Fan", description: "High-efficiency industrial cooling fan", hsn: "8414", unitPrice: 12000, qty: 2, taxRate: 18 }),
    ...generateIndividualAssets("po_1001", { id: "line_2", item: "Motor Bearings", description: "Premium quality motor bearings set", hsn: "8482", unitPrice: 1500, qty: 6, taxRate: 18 }),
  ],
};

// Actions
type Action =
  | { type: "ADD_SUPPLIER"; supplier: Omit<Supplier, "id"> }
  | { type: "ADD_CUSTOMER"; customer: Omit<Customer, "id"> }
  | { type: "UPDATE_SUPPLIER"; supplierId: ID; patch: Partial<Supplier> }
  | { type: "UPDATE_CUSTOMER"; customerId: ID; patch: Partial<Customer> }
  | { type: "CREATE_PO"; po: Omit<PurchaseOrder, "id" | "createdAt" | "updatedAt" | "status"> }
  | { type: "UPDATE_PO"; poId: ID; patch: Partial<PurchaseOrder> }
  | { type: "UPDATE_PO_STATUS"; poId: ID; status: POStatus; comments?: string }
  | { type: "UPDATE_PO_LINE"; poId: ID; lineId: ID; patch: Partial<AssetLine> }
  | { type: "ADD_INVOICE"; invoice: Omit<Invoice, "id" | "status">; selectedAssetIds: ID[] }
  | { type: "UPDATE_INVOICE"; invoiceId: ID; patch: Partial<Invoice> }
  | { type: "SAVE_DRAFT_INVOICE"; invoiceId: ID }
  | { type: "DELETE_INVOICE"; invoiceId: ID }
  | { type: "ADD_PAYMENT"; payment: Omit<Payment, "id"> }
  | { type: "UPDATE_PAYMENT"; paymentId: ID; patch: Partial<Payment> }
  | { type: "ADD_PO_ATTACHMENT"; poId: ID; url: string }
  | { type: "REMOVE_PO_ATTACHMENT"; poId: ID; index: number }
  | { type: "UPDATE_INDIVIDUAL_ASSET"; assetId: ID; patch: Partial<IndividualAsset> };

function reducer(state: State, action: Action): State {
  // Ensure individualAssets exists (for backward compatibility)
  const currentState = {
    ...state,
    individualAssets: state.individualAssets || []
  };
  
  switch (action.type) {
    case "ADD_SUPPLIER": {
      const s: Supplier = { id: `sup_${uuid()}` as ID, ...action.supplier };
      return { ...currentState, suppliers: [s, ...currentState.suppliers] };
    }
    case "ADD_CUSTOMER": {
      const c: Customer = { id: `cust_${uuid()}`, ...action.customer };
      return { ...currentState, customers: [c, ...currentState.customers] };
    }
    case "UPDATE_SUPPLIER": {
      return {
        ...currentState,
        suppliers: currentState.suppliers.map((s) => s.id === action.supplierId ? { ...s, ...action.patch } : s),
      };
    }
    case "UPDATE_CUSTOMER": {
      return {
        ...currentState,
        customers: currentState.customers.map((c) => c.id === action.customerId ? { ...c, ...action.patch } : c),
      };
    }
    case "CREATE_PO": {
      const poId = `po_${uuid()}`;
      const po: PurchaseOrder = {
        id: poId,
        status: "draft",
        createdAt: now(),
        updatedAt: now(),
        approvals: { underwriter: false },
        ...action.po,
      };
      
      // Generate individual assets for each asset line
      const newIndividualAssets: IndividualAsset[] = [];
      po.lines.forEach(line => {
        newIndividualAssets.push(...generateIndividualAssets(poId, line));
      });
      
      return { 
        ...currentState, 
        purchaseOrders: [po, ...currentState.purchaseOrders],
        individualAssets: [...currentState.individualAssets, ...newIndividualAssets]
      };
    }
    case "UPDATE_PO": {
      return {
        ...currentState,
        purchaseOrders: currentState.purchaseOrders.map((p) =>
          p.id === action.poId ? { ...p, ...action.patch, updatedAt: now() } : p
        ),
      };
    }
    case "UPDATE_PO_STATUS": {
      return {
        ...currentState,
        purchaseOrders: currentState.purchaseOrders.map((p) =>
          p.id === action.poId
            ? {
                ...p,
                status: action.status,
                approvals:
                  action.status === "approved"
                    ? { ...(p.approvals || { underwriter: false }), underwriter: true, comments: action.comments }
                    : p.approvals,
                updatedAt: now(),
              }
            : p
        ),
      };
    }
    case "ADD_INVOICE": {
      const inv: Invoice = { id: `inv_${uuid()}`, status: "draft", ...action.invoice };
      
      // Update individual assets with invoice ID when they are included in an invoice
      const updatedIndividualAssets = currentState.individualAssets.map(asset => 
        action.selectedAssetIds.includes(asset.id) 
          ? { ...asset, invoiceId: inv.id }
          : asset
      );
      
      return { 
        ...currentState, 
        invoices: [inv, ...currentState.invoices],
        individualAssets: updatedIndividualAssets
      };
    }
    case "UPDATE_INVOICE": {
      return {
        ...currentState,
        invoices: currentState.invoices.map((i) => i.id === action.invoiceId ? { ...i, ...action.patch } : i),
      };
    }
    case "SAVE_DRAFT_INVOICE": {
      return {
        ...currentState,
        invoices: currentState.invoices.map((i) => (i.id === action.invoiceId ? { ...i, status: "pending" } : i)),
      };
    }
    case "DELETE_INVOICE": {
      const paymentsToRemove = currentState.payments.filter((p) => p.invoiceId === action.invoiceId).map((p) => p.id);
      return {
        ...currentState,
        invoices: currentState.invoices.filter((i) => i.id !== action.invoiceId),
        payments: currentState.payments.filter((p) => !paymentsToRemove.includes(p.id)),
      };
    }
    case "ADD_PAYMENT": {
      const payment: Payment = { id: `pay_${uuid()}`, ...action.payment };
      
      // Create new payments array with the added payment
      const newPayments = [payment, ...currentState.payments];
      
      // Auto update invoice status based on total payments
      const inv = currentState.invoices.find((i) => i.id === action.payment.invoiceId);
      let nextInvoices = currentState.invoices;
      
      if (inv) {
        // Calculate total paid amount including the new payment
        const totalPaid = newPayments
          .filter((p) => p.invoiceId === inv.id)
          .reduce((sum, p) => sum + p.amount, 0);
        
        const totalDue = inv.amount + inv.tax;
        const tolerance = 0.01; // 1 paisa tolerance for rounding
        
        // Determine new invoice status
        let newStatus: Invoice['status'] = inv.status;
        if (totalPaid >= totalDue - tolerance) {
          newStatus = "paid";
        } else if (totalPaid > tolerance) {
          newStatus = "partial";
        } else {
          newStatus = "pending";
        }
        
        // Update invoice status if changed
        if (newStatus !== inv.status) {
          nextInvoices = nextInvoices.map((i) => 
            i.id === inv.id ? { ...i, status: newStatus } : i
          );
        }
      }
      
      return { ...currentState, invoices: nextInvoices, payments: newPayments };
    }
    case "UPDATE_PAYMENT": {
      const updatedPayments = currentState.payments.map((p) => 
        p.id === action.paymentId ? { ...p, ...action.patch } : p
      );
      
      // Find the affected invoice and recalculate status
      const payment = currentState.payments.find(p => p.id === action.paymentId);
      let nextInvoices = currentState.invoices;
      
      if (payment) {
        const inv = currentState.invoices.find((i) => i.id === payment.invoiceId);
        if (inv) {
          const totalPaid = updatedPayments
            .filter((p) => p.invoiceId === inv.id)
            .reduce((sum, p) => sum + p.amount, 0);
          
          const totalDue = inv.amount + inv.tax;
          const tolerance = 0.01;
          
          let newStatus: Invoice['status'] = "pending";
          if (totalPaid >= totalDue - tolerance) {
            newStatus = "paid";
          } else if (totalPaid > tolerance) {
            newStatus = "partial";
          }
          
          nextInvoices = nextInvoices.map((i) => 
            i.id === inv.id ? { ...i, status: newStatus } : i
          );
        }
      }
      
      return { ...currentState, invoices: nextInvoices, payments: updatedPayments };
    }
    case "ADD_PO_ATTACHMENT": {
      return {
        ...currentState,
        purchaseOrders: currentState.purchaseOrders.map((p) =>
          p.id === action.poId ? { ...p, attachments: [...(p.attachments || []), action.url], updatedAt: now() } : p
        ),
      };
    }
    case "UPDATE_PO_LINE": {
      return {
        ...state,
        purchaseOrders: state.purchaseOrders.map((p) =>
          p.id === action.poId
            ? {
                ...p,
                updatedAt: now(),
                lines: p.lines.map((l) => (l.id === action.lineId ? { ...l, ...action.patch } : l)),
              }
            : p
        ),
      };
    }
    case "REMOVE_PO_ATTACHMENT": {
      return {
        ...currentState,
        purchaseOrders: currentState.purchaseOrders.map((p) =>
          p.id === action.poId 
            ? { 
                ...p, 
                attachments: p.attachments?.filter((_, index) => index !== action.index),
                updatedAt: now() 
              } 
            : p
        ),
      };
    }
    case "UPDATE_INDIVIDUAL_ASSET": {
      return {
        ...currentState,
        individualAssets: currentState.individualAssets.map((asset) =>
          asset.id === action.assetId ? { ...asset, ...action.patch } : asset
        ),
      };
    }
    default:
      return currentState;
  }
}

interface StoreContextType extends State {
  createPO: (po: Omit<PurchaseOrder, "id" | "createdAt" | "updatedAt" | "status">) => void;
  updatePO: (poId: ID, patch: Partial<PurchaseOrder>) => void;
  updatePOStatus: (poId: ID, status: POStatus, comments?: string) => void;
  updatePOLine: (poId: ID, lineId: ID, patch: Partial<AssetLine>) => void;
  addInvoice: (invoice: Omit<Invoice, "id" | "status">, selectedAssetIds: ID[]) => void;
  updateInvoice: (invoiceId: ID, patch: Partial<Invoice>) => void;
  saveDraftInvoice: (invoiceId: ID) => void;
  deleteInvoice: (invoiceId: ID) => void;
  addPayment: (payment: Omit<Payment, "id">) => void;
  updatePayment: (paymentId: ID, patch: Partial<Payment>) => void;
  addPoAttachment: (poId: ID, url: string) => void;
  removePoAttachment: (poId: ID, index: number) => void;
  addSupplier: (supplier: Omit<Supplier, "id">) => void;
  updateSupplier: (supplierId: ID, patch: Partial<Supplier>) => void;
  addCustomer: (customer: Omit<Customer, "id">) => void;
  updateCustomer: (customerId: ID, patch: Partial<Customer>) => void;
  updateIndividualAsset: (assetId: ID, patch: Partial<IndividualAsset>) => void;
}

const StoreContext = createContext<StoreContextType | null>(null);

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const api = useMemo<StoreContextType>(() => ({
    ...state,
    createPO: (po) => dispatch({ type: "CREATE_PO", po }),
    updatePO: (poId, patch) => dispatch({ type: "UPDATE_PO", poId, patch }),
    updatePOStatus: (poId, status, comments) => dispatch({ type: "UPDATE_PO_STATUS", poId, status, comments }),
    updatePOLine: (poId, lineId, patch) => dispatch({ type: "UPDATE_PO_LINE", poId, lineId, patch }),
    addInvoice: (invoice, selectedAssetIds) => dispatch({ type: "ADD_INVOICE", invoice, selectedAssetIds }),
    updateInvoice: (invoiceId, patch) => dispatch({ type: "UPDATE_INVOICE", invoiceId, patch }),
    saveDraftInvoice: (invoiceId) => dispatch({ type: "SAVE_DRAFT_INVOICE", invoiceId }),
    deleteInvoice: (invoiceId) => dispatch({ type: "DELETE_INVOICE", invoiceId }),
    addPayment: (payment) => dispatch({ type: "ADD_PAYMENT", payment }),
    updatePayment: (paymentId, patch) => dispatch({ type: "UPDATE_PAYMENT", paymentId, patch }),
    addPoAttachment: (poId, url) => dispatch({ type: "ADD_PO_ATTACHMENT", poId, url }),
    removePoAttachment: (poId, index) => dispatch({ type: "REMOVE_PO_ATTACHMENT", poId, index }),
    addSupplier: (supplier) => dispatch({ type: "ADD_SUPPLIER", supplier }),
    updateSupplier: (supplierId, patch) => dispatch({ type: "UPDATE_SUPPLIER", supplierId, patch }),
    addCustomer: (customer) => dispatch({ type: "ADD_CUSTOMER", customer }),
    updateCustomer: (customerId, patch) => dispatch({ type: "UPDATE_CUSTOMER", customerId, patch }),
    updateIndividualAsset: (assetId, patch) => dispatch({ type: "UPDATE_INDIVIDUAL_ASSET", assetId, patch }),
  }), [state]);

  return <StoreContext.Provider value={api}>{children}</StoreContext.Provider>;
};

export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
}